import { NodeDefinition } from 'Automations/nodeDefinitions/types'
import { ReviewContractEditorNode } from './reviewContract.editorNode'
import { ReviewIcon } from '../review.icon'
import { ReviewContractConfigForm } from './reviewContract.configForm'
import { ReviewType } from '..'
import {
  approversRequired,
  handlesConnected,
  reviewTypeRequired
} from '../review.validators'
import {
  getClosestMatches,
  semanticTypes
} from 'Automations/nodeDefinitions/semanticTypes'
import {
  createSimpleTask,
  getTaskReferenceName,
  wrapVariables
} from 'Automations/nodeDefinitions/conductor'
import { ReviewContractExecutionSummary } from './ReviewContract.executionSummary'
import { contractReviewManualTask } from './manualTask'
import { TASK_EXECUTORS } from 'Automations/automations.constants'

export type Config = {
  name: string
  reviewType: ReviewType
  approverUserIds: string[]
}

type Inputs =
  | 'assetId'
  | 'billingSchedule'
  | 'contract'
  | 'customer'
  | 'requestorId'

type Outputs = 'customerId' | 'billingSchedule'

type TaskName = 'manualReview'

const taskReferenceBuilder = getTaskReferenceName<TaskName>('REVIEW_CONTRACT')

export const reviewContractNodeDefinition: NodeDefinition<
  Config,
  Inputs,
  Outputs
> = {
  name: 'Review',
  description: 'Contract',
  editorNode: ReviewContractEditorNode,
  configForm: ReviewContractConfigForm,
  icon: ReviewIcon,
  defaultConfig: {
    name: '',
    reviewType: 'CONTRACT',
    approverUserIds: []
  },
  validation: {
    node: { ERROR: [], WARN: [] },
    config: {
      name: { ERROR: [], WARN: [] },
      reviewType: {
        ERROR: [reviewTypeRequired],
        WARN: []
      },
      approverUserIds: {
        ERROR: [approversRequired],
        WARN: []
      }
    },
    handles: { ERROR: [handlesConnected], WARN: [] }
  },
  inputMapping: ({ upstreamNodes }) => {
    return getClosestMatches<Inputs>(upstreamNodes)({
      assetId: semanticTypes.sequence.asset.id,
      billingSchedule: semanticTypes.sequence.billingSchedule,
      contract: semanticTypes.sequence.contract.fullContract,
      customer: semanticTypes.sequence.customer.customer,
      requestorId: semanticTypes.sequence.requestorId
    })
  },
  outputMapping: nodeId => {
    const taskReferenceName = taskReferenceBuilder(nodeId)

    return {
      customerId: {
        id: `${taskReferenceName('manualReview')}.output.customerId`,
        semanticType: semanticTypes.sequence.customer.id
      },
      billingSchedule: {
        id: `${taskReferenceName('manualReview')}.output.billingSchedule`,
        semanticType: semanticTypes.sequence.billingSchedule
      }
    }
  },
  taskTransformer: ({ nodeId, inputMapping, config }) => {
    const taskReferenceName = taskReferenceBuilder(nodeId)

    return [
      createSimpleTask({
        name: 'manual_task',
        taskReferenceName: taskReferenceName('manualReview'),
        inputParameters: {
          taskExecutorConfiguration: {
            version: 1,
            executor: TASK_EXECUTORS.reviewContract
          },
          data: {
            ...wrapVariables({
              assetId: inputMapping.assetId,
              billingSchedule: inputMapping.billingSchedule,
              contract: inputMapping.contract,
              customer: inputMapping.customer
            }),
            assignmentRules: {
              type: 'users',
              users: config.approverUserIds
            }
          }
        }
      })
    ]
  },
  executionSummary: ReviewContractExecutionSummary,
  manualTask: {
    type: 'CONTRACT_REVIEW',
    ...contractReviewManualTask
  }
}
